import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader, Text } from '@vibe/core';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import type { PickedBoard } from './BoardPicker';
import mondaySdk from 'monday-sdk-js';

const monday = mondaySdk();

// ─── Constants ───────────────────────────────────────────────────────────────

const BOARDS_QUERY = `
  query GetBoards($limit: Int!, $page: Int!) {
    boards(limit: $limit, page: $page, order_by: used_at) {
      id
      name
      type
    }
  }
`;

const DEFAULT_LIMIT = 10;
const SEARCH_LIMIT = 100;
const SEARCH_MIN_CHARS = 2;
const MAX_RESULTS = 20;
const DEBOUNCE_MS = 300;

// ─── Props ───────────────────────────────────────────────────────────────────

interface MultiBoardPickerProps {
  /** Currently selected boards */
  value: PickedBoard[];
  /** Called when the selection changes (board added or removed) */
  onChange: (boards: PickedBoard[]) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Disable the picker */
  disabled?: boolean;
}

// ─── Component ───────────────────────────────────────────────────────────────

const MultiBoardPicker: React.FC<MultiBoardPickerProps> = ({
  value,
  onChange,
  placeholder = 'Search for a board…',
  disabled = false,
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<PickedBoard[]>([]);
  const [defaultBoards, setDefaultBoards] = useState<PickedBoard[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedQuery = useDebouncedValue(query, DEBOUNCE_MS);

  const selectedIds = new Set(value.map((b) => b.id));

  // Load default boards (first 10, sorted by recently used) on mount
  useEffect(() => {
    let cancelled = false;

    async function loadDefaults() {
      setIsLoadingDefaults(true);
      try {
        const response = await monday.api(BOARDS_QUERY, {
          variables: { limit: DEFAULT_LIMIT, page: 1 },
        });
        const data = response.data as {
          boards?: Array<{ id: string | number; name: string; type?: string }>;
        };
        if (!cancelled) {
          setDefaultBoards(
            (data.boards ?? [])
              .filter((b) => b.type !== 'sub_items_board')
              .map((b) => ({
                id: String(b.id),
                name: b.name,
              })),
          );
        }
      } catch {
        // Silently fail – the user can still type to search
      } finally {
        if (!cancelled) setIsLoadingDefaults(false);
      }
    }

    loadDefaults();
    return () => {
      cancelled = true;
    };
  }, []);

  // Search boards when debounced query changes (2+ characters)
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < SEARCH_MIN_CHARS) {
      setResults([]);
      return;
    }

    let cancelled = false;

    async function search() {
      setIsSearching(true);
      try {
        const response = await monday.api(BOARDS_QUERY, {
          variables: { limit: SEARCH_LIMIT, page: 1 },
        });
        const data = response.data as {
          boards?: Array<{ id: string | number; name: string; type?: string }>;
        };

        if (cancelled) return;

        const lowerQ = debouncedQuery.toLowerCase();
        const matched: PickedBoard[] = [];

        for (const b of data.boards ?? []) {
          if (b.type === 'sub_items_board') continue;
          if (b.name.toLowerCase().includes(lowerQ)) {
            matched.push({ id: String(b.id), name: b.name });
          }
          if (matched.length >= MAX_RESULTS) break;
        }

        setResults(matched);
      } catch {
        // Silently fail
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    }

    search();
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputFocus = useCallback(() => {
    if (!disabled) setIsOpen(true);
  }, [disabled]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(e.target.value);
      if (!isOpen) setIsOpen(true);
    },
    [isOpen],
  );

  const handleSelect = useCallback(
    (board: PickedBoard) => {
      if (selectedIds.has(board.id)) return;
      onChange([...value, board]);
    },
    [value, onChange, selectedIds],
  );

  const handleRemove = useCallback(
    (boardId: string) => {
      onChange(value.filter((b) => b.id !== boardId));
    },
    [value, onChange],
  );

  // Filter out already-selected boards from display
  const isSearchMode = debouncedQuery.length >= SEARCH_MIN_CHARS;
  const sourceItems = isSearchMode ? results : defaultBoards;
  const displayItems = sourceItems.filter((b) => !selectedIds.has(b.id));

  return (
    <div className="rp-multi-board-picker" ref={containerRef}>
      {/* Selected boards as chips */}
      {value.length > 0 && (
        <div className="rp-multi-board-picker__chips">
          {value.map((board) => (
            <div key={board.id} className="rp-multi-board-picker__chip">
              <span className="rp-multi-board-picker__chip-name">
                {board.name}
              </span>
              {!disabled && (
                <button
                  className="rp-multi-board-picker__chip-remove"
                  onClick={() => handleRemove(board.id)}
                  type="button"
                  aria-label={`Remove ${board.name}`}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Search input */}
      <input
        ref={inputRef}
        className="rp-multi-board-picker__input"
        type="text"
        value={query}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
      />

      {/* Dropdown */}
      {isOpen && (
        <div className="rp-multi-board-picker__dropdown">
          {/* Loading indicator */}
          {(isSearching || isLoadingDefaults) && (
            <div className="rp-multi-board-picker__dropdown-status">
              <Loader size={Loader.sizes.XS} />
              <span>
                {isSearchMode ? 'Searching…' : 'Loading boards…'}
              </span>
            </div>
          )}

          {/* Hint text */}
          {!isSearching &&
            !isLoadingDefaults &&
            !isSearchMode &&
            defaultBoards.length > 0 && (
              <div className="rp-multi-board-picker__dropdown-hint">
                <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
                  Recently used · type 2+ characters to search
                </Text>
              </div>
            )}

          {/* No results */}
          {!isSearching &&
            !isLoadingDefaults &&
            isSearchMode &&
            displayItems.length === 0 && (
              <div className="rp-multi-board-picker__dropdown-status">
                <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
                  No boards found
                </Text>
              </div>
            )}

          {/* Board options */}
          {displayItems.map((board) => (
            <button
              key={board.id}
              className="rp-multi-board-picker__option"
              onClick={() => handleSelect(board)}
              type="button"
            >
              <span className="rp-multi-board-picker__option-name">
                {board.name}
              </span>
              <span className="rp-multi-board-picker__option-id">
                ID: {board.id}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiBoardPicker;
